/**
 * Chat tenant settings and usage (REQ-CHAT-042).
 *
 * Consumes `/api/v1/tenants/{tenant_id}/settings/chat`; the backend enforces
 * `Module:capabilities#{view,admin}` and the retention/quota rules.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';

export interface ChatUsage {
  chat_content_bytes: number;
  storage_used_bytes: number;
  storage_quota_bytes: number;
}

export interface ChatSettings {
  retention_days: number;
  recycle_bin_retention_days: number;
  usage: ChatUsage;
}

const queryKey = (tenantId: string) => ['chat-settings', tenantId];

export function useChatSettings() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id ?? '';

  return useQuery({
    queryKey: queryKey(tenantId),
    queryFn: async () => {
      const { data } = await apiClient.get<ChatSettings>(
        `/api/v1/tenants/${tenantId}/settings/chat`,
      );
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useUpdateChatSettings() {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id ?? '';

  return useMutation({
    mutationFn: async (retentionDays: number) => {
      const { data } = await apiClient.put<ChatSettings>(
        `/api/v1/tenants/${tenantId}/settings/chat`,
        { retention_days: retentionDays },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKey(tenantId) }),
  });
}
